import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, Copy, EllipsisVertical, FilePen, MapPin, Plus, Search, Trash2 } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { eventDates, eventPlaces, eventSeasons, formatWhen, statusLabel } from '../lib/format'
import type { Category, EventDraft, EventListItem, EventStatus, Place } from '../types'
import { LoadingState } from './LoadingState'

type Props = {
  places: Place[]
  categories: Category[]
  onCreate: () => void
  onOpenEvent: (id: string) => void
  onOpenDraft: (id: string) => void
  // A new event on this one's content (PRD.md, item 74).
  onDuplicate: (id: string) => void
  onNotice: (message: string) => void
  onError: (message: string) => void
}

// Where and when, as the app's cards say it; with two or more seasons, each
// with its own days and place, the first two and how many more (PRD.md, item
// 118).
function CardWhere({ event }: { event: EventListItem }) {
  const seasons = eventSeasons(event)
  if (!seasons.length) {
    return <>
      <span className="event-card__place"><MapPin size={15} /><span>{eventPlaces(event)}</span></span>
      <span><CalendarDays size={15} /> {eventDates(event)}</span>
    </>
  }
  const more = seasons.length - 2
  return <>
    {seasons.slice(0, 2).map((season, index) => (
      <span className="event-card__place event-card__season" key={index}>
        <CalendarDays size={15} /><span className="event-card__season-dates">{season.dates}</span><span>· {season.place}</span>
      </span>
    ))}
    {more > 0 && <span className="event-card__more">+{more} {more === 1 ? 'local' : 'locais'}</span>}
  </>
}

export function EventList({ places, categories, onCreate, onOpenEvent, onOpenDraft, onDuplicate, onNotice, onError }: Props) {
  const [items, setItems] = useState<EventListItem[]>([])
  const [drafts, setDrafts] = useState<EventDraft[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<EventStatus | 'all'>('all')
  const [temporal, setTemporal] = useState<'all' | 'active' | 'ended'>('all')
  const [city, setCity] = useState('')
  const [category, setCategory] = useState('')
  const [sort, setSort] = useState<'updated' | 'date'>('updated')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // The card whose ⋮ menu is open (PRD.md, item 74).
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const pageSize = 24
  const cities = useMemo(() => [...new Set(places.map((place) => place.city?.trim()).filter((name): name is string => Boolean(name)))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR')), [places])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => setPage(1), [debouncedSearch, status, temporal, city, category, sort])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        status,
        temporal,
        sort,
      })
      if (debouncedSearch) params.set('search', debouncedSearch)
      if (city) params.set('city', city)
      if (category) params.set('category', category)
      const result = await cmsApi.listEvents(params)
      setItems(result.items)
      setTotal(result.total)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar os eventos.')
    } finally {
      setLoading(false)
    }
  }, [category, city, debouncedSearch, page, sort, status, temporal])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (!menuFor) return
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuFor(null) }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [menuFor])
  useEffect(() => {
    cmsApi.listDrafts().then((result) => setDrafts(result.items)).catch(() => setDrafts([]))
  }, [])

  // Closed as discarded and kept, as from the editor (PRD.md, items 19 and 73).
  const deleteDraft = async (draft: EventDraft) => {
    if (!window.confirm(`Excluir o rascunho “${draft.title || 'Sem título'}”? Ele sai da lista de rascunhos.`)) return
    try {
      await cmsApi.discardDraft(draft.id)
      setDrafts((current) => current.filter((item) => item.id !== draft.id))
      onNotice('Rascunho excluído.')
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível excluir o rascunho.')
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="page-stack">
      <section className="page-heading page-heading--with-action">
        <div>
          <h1>Eventos</h1>
          <p>Todos os eventos do Guve. Antes de criar, busque pelo título: se ele já existe, abra e acrescente as datas.</p>
        </div>
        <button className="button button--primary" onClick={onCreate}><Plus size={18} /> Novo evento</button>
      </section>

      {drafts.length > 0 && (
        <section className="panel panel--flush" aria-label="Rascunhos">
          <div className="panel__heading"><div><h2>Rascunhos</h2><p>Eventos em criação, ainda fora do app. Abra um para continuar de onde parou.</p></div></div>
          <div className="compact-event-list">
            {drafts.map((draft) => (
              <div className="draft-row" key={draft.id}>
                <button className="compact-event" onClick={() => onOpenDraft(draft.id)}>
                  <span className="compact-event__media"><FilePen size={18} /></span>
                  <span className="compact-event__main">
                    <strong>{draft.title || 'Sem título'}</strong>
                    <small>Criado por {draft.created_by_email}{draft.updated_by_email !== draft.created_by_email ? ` · editado por ${draft.updated_by_email}` : ''}</small>
                  </span>
                  <span className="compact-event__date">{formatWhen(draft.updated_at)}</span>
                </button>
                <button className="icon-button icon-button--danger" aria-label={`Excluir rascunho ${draft.title || 'Sem título'}`} title="Excluir rascunho" onClick={() => void deleteDraft(draft)}><Trash2 size={17} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="panel panel--flush">
        <div className="filter-bar filter-bar--stacked">
          <label className="search-field">
            <Search size={18} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por título ou local" />
          </label>
          <select value={status} onChange={(event) => setStatus(event.target.value as EventStatus | 'all')}>
            <option value="all">Todos os status</option>
            <option value="published">Publicados</option>
            <option value="canceled">Cancelados</option>
            <option value="deleted">Excluídos</option>
          </select>
          <select value={temporal} onChange={(event) => setTemporal(event.target.value as typeof temporal)}>
            <option value="all">Todas as datas</option>
            <option value="active">Ativos e futuros</option>
            <option value="ended">Encerrados</option>
          </select>
          <select aria-label="Cidade" value={city} onChange={(event) => setCity(event.target.value)}>
            <option value="">Todas as cidades</option>
            {cities.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <select aria-label="Categoria" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">Todas as categorias</option>
            {categories.map((item) => <option key={item.name} value={item.name}>{item.display_name || item.name}</option>)}
          </select>
          <select aria-label="Ordenar" value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}>
            <option value="updated">Ordem: última edição</option>
            <option value="date">Ordem: data do evento</option>
          </select>
        </div>

        {loading ? <LoadingState label="Carregando catálogo…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : items.length ? (
          <div className="event-grid">
            {items.map((event) => (
              <div className="event-tile" key={event.id}>
                <button className="event-card" onClick={() => onOpenEvent(event.id)}>
                  <span className="event-card__image">
                    {/* Cropped as the app's card, around the photo's framing (PRD.md, item 40). */}
                    {event.image_url ? <img src={event.image_url} alt="" style={{ objectPosition: `${(event.image_focus_x ?? 0.5) * 100}% ${(event.image_focus_y ?? 0.5) * 100}%` }} /> : <CalendarDays size={28} />}
                    {/* Only what departs from the norm: a published event needs no badge. */}
                    {event.status !== 'published' && <span className={`status-pill status-pill--${event.status}`}>{statusLabel(event.status)}</span>}
                  </span>
                  <span className="event-card__content">
                    <small>{event.category}</small>
                    <strong>{event.title}</strong>
                    <CardWhere event={event} />
                  </span>
                </button>
                {/* Duplicating is a choice of its own, in a menu, as Google Drive and
                    Eventbrite do: the card itself always opens the event. */}
                <button className="icon-button event-tile__more" aria-label={`Mais ações para ${event.title}`} aria-haspopup="menu" aria-expanded={menuFor === event.id}
                  onClick={() => setMenuFor(menuFor === event.id ? null : event.id)}><EllipsisVertical size={16} /></button>
                {menuFor === event.id && <>
                  <div className="menu-backdrop" onClick={() => setMenuFor(null)} />
                  <div className="card-menu" role="menu" aria-label={`Ações para ${event.title}`}>
                    <button role="menuitem" autoFocus onClick={() => { setMenuFor(null); onDuplicate(event.id) }}><Copy size={16} /> Duplicar evento</button>
                  </div>
                </>}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state"><CalendarDays size={30} /><h3>Nenhum evento encontrado</h3><p>Revise os filtros ou crie um evento.</p></div>
        )}

        <div className="pagination">
          <span>{total.toLocaleString('pt-BR')} eventos</span>
          <div>
            <button className="icon-button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={18} /></button>
            <span>{page} de {pageCount}</span>
            <button className="icon-button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>
    </div>
  )
}
